import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionBar, Button, Dialog, Modal, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../../components/GenericTable'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

/**
 * @fileoverview Loop Participants Tab
 *
 * Manage loop participants who will receive loop execution invitations
 */

interface LoopParticipant {
	id: string
	loopId: string
	userId: string
	firstName: string
	lastName: string
	email: string
	externalId: string
	active: boolean
}

interface User {
	id: string
	organizationId: string
	email: string
	firstName: string
	lastName: string
	externalId: string
	active: boolean
}

type ParticipantSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

type UserSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

export function LoopParticipantsTab() {
	const { loopId } = useParams<{ loopId: string }>()
	const { organization } = sessionStore()
	const organizationId = organization?.id
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])
	const [selectedParticipantsToDelete, setSelectedParticipantsToDelete] = useState<LoopParticipant[]>([])
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const addParticipantsMutation = useMutation({
		...trpc.client.loops.addParticipants.mutationOptions(),
		onSuccess: () => {
			showToast('User(s) added as participant(s)')
			setIsModalOpen(false)
			setSelectedUsers([])
		},
		onError: () => {
			showToast('Something went wrong, the user(s) could not be added as participant(s)', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.client.loops.searchParticipants.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const deleteParticipantsMutation = useMutation({
		...trpc.client.loops.deleteParticipants.mutationOptions(),
		onSuccess: () => {
			showToast('Participant(s) removed')
			setSelectedParticipantsToDelete([])
			setIsDeleteDialogOpen(false)
		},
		onError: () => {
			showToast('Something went wrong, the participant(s) could not be removed', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.client.loops.searchParticipants.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleOpenModal = () => {
		setIsModalOpen(true)
	}

	useEffect(() => {
		if (isModalOpen) {
			queryClient.invalidateQueries({
				queryKey: [['loops', 'searchPossibleParticipants']],
				exact: false,
				refetchType: 'all',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModalOpen, queryClient.invalidateQueries])

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedUsers([])
	}

	const handleAddSelected = () => {
		if (!loopId || !organizationId) return
		addParticipantsMutation.mutate({
			loopId,
			organizationId,
			userIds: selectedUsers.map((user) => user.id),
		})
	}

	const handleDeleteSelected = () => {
		if (!loopId || !organizationId) return
		deleteParticipantsMutation.mutate({
			loopId,
			organizationId,
			userIds: selectedParticipantsToDelete.map((participant) => participant.userId),
		})
	}

	const handleOpenDeleteDialog = () => {
		setIsDeleteDialogOpen(true)
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
	}

	return (
		<div className='mt-6'>
			<div className='px-6 max-w-6xl mb-4 flex justify-between items-center'>
				<Text variant='small' className='text-gray-600'>
					Loop participants will receive invitations when executions are launched. Add users who should participate in
					this loop.
				</Text>
				<div className='flex gap-2'>
					<Button variant='primary' onClick={handleOpenModal}>
						Add Participant
					</Button>
					<Button
						variant='destructive'
						onClick={handleOpenDeleteDialog}
						disabled={selectedParticipantsToDelete.length === 0}>
						Remove Selected
					</Button>
				</div>
			</div>

			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<LoopParticipant, ParticipantSearchInput>
					queryOptionsGenerator={(options: ParticipantSearchInput) => ({
						...trpc.client.loops.searchParticipants.queryOptions({
							...options,
							organizationId: organizationId as string,
							loopId: loopId as string,
						}),
						enabled: !!loopId,
					})}
					selectable
					onSelectionChange={setSelectedParticipantsToDelete}
					columns={[
						{
							field: 'firstName',
							label: 'Name',
							colSpan: 2,
							render: (participant: LoopParticipant) => (
								<Text variant='xsmall' weight='regular'>
									{`${participant.firstName} ${participant.lastName}`}
								</Text>
							),
						},
						{
							field: 'externalId',
							label: 'ID',
							colSpan: 1,
						},
						{
							field: 'email',
							label: 'Email',
							colSpan: 2,
						},
					]}
					emptyState={{
						title: 'No participants found',
						description: 'Add users who should participate in this loop',
						actionLabel: 'Add Participant',
						onAction: handleOpenModal,
					}}
				/>
			</div>

			{/* Add Participants Modal */}
			<Modal isOpen={isModalOpen} onClose={handleCloseModal} title='Add Loop Participants'>
				<div className='flex flex-col gap-4'>
					<Text variant='small' className='text-gray-600'>
						Select users to add as participants for this loop. Participants will receive invitations when executions are
						launched.
					</Text>

					<GenericTable<User, UserSearchInput>
						queryOptionsGenerator={(options: UserSearchInput) => ({
							...trpc.client.loops.searchPossibleParticipants.queryOptions({
								...options,
								organizationId: organizationId as string,
								loopId: loopId as string,
							}),
							enabled: !!loopId,
						})}
						selectable
						onSelectionChange={setSelectedUsers}
						columns={[
							{
								field: 'firstName',
								label: 'Name',
								colSpan: 2,
								render: (user: User) => (
									<Text variant='xsmall' weight='regular'>
										{`${user.firstName} ${user.lastName}`}
									</Text>
								),
							},
							{
								field: 'externalId',
								label: 'ID',
								colSpan: 1,
							},
							{
								field: 'email',
								label: 'Email',
								colSpan: 2,
							},
						]}
						emptyState={{
							title: 'No users available to add',
							description: 'All eligible users are already loop participants',
						}}
					/>

					<ActionBar
						variant='modal'
						rightActions={[
							{
								label: 'Cancel',
								onClick: handleCloseModal,
								variant: 'secondary',
							},
							{
								label: `Add ${selectedUsers.length} Participant${selectedUsers.length !== 1 ? 's' : ''}`,
								onClick: handleAddSelected,
								variant: 'primary',
								disabled: selectedUsers.length === 0 || addParticipantsMutation.isPending,
							},
						]}
					/>
				</div>
			</Modal>

			{/* Delete Confirmation Dialog */}
			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteSelected}
				title='Remove Loop Participants'
				message={`Are you sure you want to remove ${selectedParticipantsToDelete.length} participant${selectedParticipantsToDelete.length !== 1 ? 's' : ''}? They will no longer receive loop execution invitations.`}
				type='delete'
				confirmLabel='Remove'
				isConfirmLoading={deleteParticipantsMutation.isPending}
			/>
		</div>
	)
}
